import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { FALLBACK_NOTICE_HOURS, noticeCutoff } from './bookingNotice';
import { FALLBACK_BUFFER_MINUTES, FALLBACK_SLOT_MINUTES } from './bookingSlots';
import { wallClockInStore } from './storeTime';

// Extend dayjs with plugins

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

// Duration enum mapping from API
export const DURATION_ENUM_MAP: Record<number, number> = {
  1: 15,   // FifteenMinutes
  2: 30,   // ThirtyMinutes  
  3: 45,   // FortyFiveMinutes
  4: 60,   // OneHour
  5: 75,   // OneHourFifteenMinutes
  6: 90,   // OneHourThirtyMinutes
  7: 105,  // OneHourFortyFiveMinutes
  8: 120,  // TwoHours
  9: 135,  // TwoHoursFifteenMinutes
  10: 150, // TwoHoursThirtyMinutes
  11: 165, // TwoHoursFortyFiveMinutes
  12: 180, // ThreeHours
  13: 195, // ThreeHoursFifteenMinutes
  14: 210, // ThreeHoursThirtyMinutes
  15: 225, // ThreeHoursFortyFiveMinutes
  16: 5,   // FiveMinutes
  17: 10   // TenMinutes
};

/**
 * Convert a pricing-option duration value to minutes.
 * Values 1-17 are enum codes (see DURATION_ENUM_MAP); values above the enum
 * range are stored as raw minutes (e.g. a manually-set 25 = 25 minutes) —
 * the backend's PriceOptionDurationHelper applies the same rule.
 * Falls back to 60 minutes for missing/invalid values.
 */
export const durationEnumToMinutes = (durationEnum?: number | null): number => {
  if (!durationEnum) return 60;
  const mapped = DURATION_ENUM_MAP[durationEnum];
  if (mapped) return mapped;
  return durationEnum > 17 ? durationEnum : 60;
};

/**
 * Convert duration enum to display format (15min, 1h, etc.)
 */
export const formatDurationFromEnum = (durationEnum: number): string => {
  const minutes = durationEnumToMinutes(durationEnum);
  if (minutes < 60) {
    return `${minutes}min`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${remainingMinutes}min`;
};

export interface BookingService {
  id: string;
  name: string;
  durationEnum: number;
  price: number;
}

export interface SelectedService {
  serviceId: string;
  petId: string;
  petName: string;
  /**
   * The pricing option behind the service — one "Full groom" has a row per
   * size, and the waitlist quotes by that row. Absent in flows that never
   * chose one (reschedule, rebook), which is why the waitlist button hides
   * there.
   */
  pricingOptionId?: string;
  /** Display only — the engine quotes from the menu, never from here. */
  serviceName?: string;
  price?: number;
}

export interface Groomer {
  id: string;
  name: string;
  role: string;
  avatar?: string;
  serviceIds: string[]; // Services this groomer can perform
}

export interface TimeSlot {
  time: string;
  available: boolean;
  groomers: string[]; // Available groomers for this slot
  disabledReason?: 'tooSoon' | null;
}

export interface Appointment {
  id: string;
  groomerId: string;
  startTime: string; // ISO string
  endTime: string;   // ISO string
  date: string;      // YYYY-MM-DD
  /** A booked pick-up or home visit's drive, kept clear before and after it (G42). */
  bufferMinutes?: number;
}

export interface BlockedTime {
  id: string;
  groomerId: string;
  startTime: string; // ISO string
  endTime: string;   // ISO string
  date: string;      // YYYY-MM-DD
}

export interface BusinessHours {
  dayOfWeek: number; // 0 = Sunday, 1 = Monday, etc.
  openTime: string;  // HH:mm format
  closeTime: string; // HH:mm format
  isClosed: boolean;
}

/**
 * Calculate total duration needed for all selected services
 */
export const calculateTotalDuration = (
  selectedServices: SelectedService[],
  services: BookingService[]
): number => {
  return selectedServices.reduce((total, selected) => {
    const service = services.find(s => s.id === selected.serviceId);
    if (service && service.durationEnum) {
      return total + durationEnumToMinutes(service.durationEnum);
    }
    return total;
  }, 0);
};

/**
 * Get groomers who can perform ALL selected services
 */
export const getEligibleGroomers = (
  selectedServices: SelectedService[],
  groomers: Groomer[]
): Groomer[] => {
  const requiredServiceIds = Array.from(new Set(selectedServices.map(s => s.serviceId)));
  
  return groomers.filter(groomer => 
    requiredServiceIds.every(serviceId => 
      groomer.serviceIds.includes(serviceId)
    )
  );
};

/**
 * Start times within business hours, every `stepMinutes` from opening (the
 * salon's time slots, G51; mpgadmin G238; 15 before it was a setting). Only
 * shows slots where the service can complete within business hours.
 */
export const generateTimeSlots = (
  date: Dayjs,
  businessHours: BusinessHours[],
  totalDurationMinutes: number = 60,
  stepMinutes: number = FALLBACK_SLOT_MINUTES
): string[] => {
  const step = stepMinutes > 0 ? stepMinutes : FALLBACK_SLOT_MINUTES;
  const dayOfWeek = date.day();
  const businessHour = businessHours.find(bh => bh.dayOfWeek === dayOfWeek);
  
  if (!businessHour || businessHour.isClosed) {
    return [];
  }
  
  const slots: string[] = [];
  const [openHour, openMinute] = businessHour.openTime.split(':').map(Number);
  const [closeHour, closeMinute] = businessHour.closeTime.split(':').map(Number);
  
  const startTime = date.hour(openHour).minute(openMinute).second(0);
  const endTime = date.hour(closeHour).minute(closeMinute).second(0);
  
  let currentTime = startTime;
  
  while (currentTime.isBefore(endTime)) {
    const serviceEndTime = currentTime.add(totalDurationMinutes, 'minute');
    
    if (serviceEndTime.isSameOrBefore(endTime)) {
      slots.push(currentTime.format('h:mm A'));
    } else {
      break;
    }
    
    currentTime = currentTime.add(step, 'minute');
  }
  
  return slots;
};

/**
 * Optimized bulk check time slot availability with custom duration.
 *
 * `noticeHours` is the salon's booking window (G15, mpgadmin G77): a slot
 * that starts sooner than now plus it is kept, disabled as too soon, on any
 * day; one that has passed is left out. `nowMs` is now, given for tests.
 * `slotMinutes` and `salonBufferMinutes` are the salon's time slots and buffer
 * after each booking (G51, mpgadmin G238).
 */
export const getOptimizedAvailableTimeSlotsWithDuration = (
  date: Dayjs,
  selectedServices: SelectedService[],
  services: BookingService[],
  groomers: Groomer[],
  appointments: Appointment[],
  blockedTimes: BlockedTime[],
  businessHours: BusinessHours[],
  totalDurationMinutes: number,
  noticeHours: number = FALLBACK_NOTICE_HOURS,
  nowMs: number = Date.now(),
  ownBufferMinutes: number = 0,
  slotMinutes: number = FALLBACK_SLOT_MINUTES,
  salonBufferMinutes: number = FALLBACK_BUFFER_MINUTES
): TimeSlot[] => {
  const eligibleGroomers = getEligibleGroomers(selectedServices, groomers);
  const timeSlots = generateTimeSlots(date, businessHours, totalDurationMinutes, slotMinutes);
  const dateString = date.format('YYYY-MM-DD');
  
  if (eligibleGroomers.length === 0) {
    return [];
  }

  // Pre-process appointments and blocked times for this date
  const todayAppointments = appointments.filter(apt => apt.date === dateString);
  const todayBlockedTimes = blockedTimes.filter(block => block.date === dateString);
  
  const availableSlots: TimeSlot[] = [];
  
  /*
   * The salon's wall clock. Slots and appointment times in this function are
   * naive Melbourne values, so comparing them against a device-local now
   * mixes two frames and hides (or reveals) today's slots by the offset.
   *
   * The booking window applies on every day, not just today: with 24 or 48
   * hours, tomorrow's morning is too soon as well (G15). Its cutoff is now
   * plus the notice in real hours, read on the salon's clock, as the API
   * counts it.
   */
  const now = wallClockInStore(nowMs);
  const cutoff = noticeCutoff(nowMs, noticeHours);

  timeSlots.forEach(timeSlot => {
    const slotStart = dayjs(`${dateString} ${timeSlot}`, 'YYYY-MM-DD h:mm A');
    const slotEnd = slotStart.add(totalDurationMinutes, 'minute');

    // Skip time slots that have already passed (only today has any: the
    // calendar doesn't open past days).
    if (slotStart.isBefore(now)) {
      return; // Skip this time slot
    }
    
    const availableGroomers: string[] = [];
    
    eligibleGroomers.forEach(groomer => {
      // Layer 2: Check for appointment conflicts using standard overlap formula
      // timeSlotStart < appointmentEnd && timeSlotEnd > appointmentStart
      const hasAppointmentConflict = todayAppointments.some(apt => {
        if (apt.groomerId !== groomer.id) return false;
        
        // A visit away keeps the drive clear on both sides: the gap is the
        // larger of this booking's buffer and the booked one's, as the API
        // checks it (G42; mpgadmin GroomerDoubleBooking). Salon to salon: the
        // salon's buffer after every booking (G51, mpgadmin G238), kept on
        // both sides so this booking neither starts in the clear time after
        // one nor ends in the clear time before the next.
        const gap = Math.max(ownBufferMinutes, apt.bufferMinutes ?? 0, salonBufferMinutes);
        const aptStart = dayjs(apt.startTime).subtract(gap, 'minute');
        const aptEnd = dayjs(apt.endTime).add(gap, 'minute');
        
        // Standard overlap check: slot overlaps appointment if slotStart < aptEnd && slotEnd > aptStart
        return slotStart.isBefore(aptEnd) && slotEnd.isAfter(aptStart);
      });
      
      // Layer 2: Check for blocked time conflicts using same overlap formula
      const hasBlockedTimeConflict = todayBlockedTimes.some(block => {
        if (block.groomerId !== groomer.id) return false;
        
        const blockStart = dayjs(block.startTime);
        const blockEnd = dayjs(block.endTime);
        
        // Standard overlap check: slot overlaps blocked time if slotStart < blockEnd && slotEnd > blockStart
        return slotStart.isBefore(blockEnd) && slotEnd.isAfter(blockStart);
      });
      
      // Layer 2: Only include groomer if they have NO conflicts
      if (!hasAppointmentConflict && !hasBlockedTimeConflict) {
        availableGroomers.push(groomer.id);
      }
    });
    
    // Layer 2: Only include time slot if at least one compatible groomer is available
    // Do NOT remove the time slot itself unless ALL compatible groomers are unavailable
    if (availableGroomers.length > 0) {
      // Slots inside the booking window are kept in the list but rendered
      // disabled, so the user sees why the day looks empty and can call the
      // shop for urgent jobs. Any day: a 48-hour notice reaches past tomorrow.
      const isTooSoon = slotStart.isBefore(cutoff);
      availableSlots.push({
        time: timeSlot,
        available: !isTooSoon,
        groomers: availableGroomers,
        disabledReason: isTooSoon ? 'tooSoon' : null,
      });
    }
  });
  
  return availableSlots;
};

/**
 * Format duration for display
 */
export const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) {
    return `${hours} hr${hours > 1 ? 's' : ''}`;
  }
  
  return `${hours} hr${hours > 1 ? 's' : ''} ${remainingMinutes} min`;
};

/**
 * Check if a date should be disabled (past dates or closed days)
 */
export const isDateDisabled = (
  date: Dayjs,
  businessHours: BusinessHours[]
): boolean => {
  // Disable past dates
  if (date.isBefore(dayjs().startOf('day'))) {
    return true;
  }

  // Today is always selectable — even if it's a closed business day — so the
  // customer can view it and use the "call the shop" / "next available" options.
  if (date.isSame(dayjs(), 'day')) {
    return false;
  }

  // Disable closed days
  const dayOfWeek = date.day();
  const businessHour = businessHours.find(bh => bh.dayOfWeek === dayOfWeek);

  return !businessHour || businessHour.isClosed;
};